/**
 * Scorecard History Section Component
 * Displays historical scorecards from other interviews/activities
 */

import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Flex,
} from '@chakra-ui/react';
import { EmptyResults, Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';
import { AppReview } from '@/assets/icons/ats/empty';
import { GroupedScorecards, ScorecardItem } from '../types';
import { useHistoryTableConfig } from './table-config';
import { InterviewType } from '@/pages/interviews-revamp/types/interview';

interface ScorecardHistorySectionProps {
  interview: InterviewType;
  historyScorecards: ScorecardItem[];
  onViewScorecard: (_: ScorecardItem) => void;
  currentInterviewScorecards: ScorecardItem[];
  transferInfo?: GroupedScorecards['transferInfo'];
}

const historySectionContent = (
  historyScorecards: ScorecardItem[],
  tableConfig: unknown[]
) => {
  return (
    <>
      {historyScorecards?.length > 0 ? (
        <Table
          columns={tableConfig}
          data={historyScorecards}
          tableId="scorecard_history"
          skipPagination={true}
          layoutStyles={{ px: '8px', py: '8px' }}
          layoutScrollContainerStyles={{
            style: { overflowX: 'auto', overflowY: 'visible' },
          }}
        />
      ) : (
        <EmptyResults
          icon={AppReview}
          wrapperProps={{ h: '200px' }}
          iconProps={{ w: '120px', h: '90px', mb: '10px' }}
          subText="NO HISTORY"
          text="No scorecard history available"
        />
      )}
    </>
  );
};

export const ScorecardHistorySection = ({
  historyScorecards,
  onViewScorecard,
  interview,
  currentInterviewScorecards,
  transferInfo,
}: ScorecardHistorySectionProps) => {
  const { feScorecardVisibility } = useKFlags();
  const tableConfig = useHistoryTableConfig(
    onViewScorecard,
    historyScorecards,
    feScorecardVisibility,
    interview,
    transferInfo
  );

  return (
    <>
      {/* If there are no current interview scorecards, then it means its a one-on-one interview and so the accordion should not be shown*/}
      {historyScorecards?.length > 0 &&
        currentInterviewScorecards?.length > 0 && (
          <AccordionItem border="none">
            <AccordionButton
              w="100%"
              _focus={{ boxShadow: 'none' }}
              borderBottom="1px solid"
              borderColor="border-color"
              justifyContent="space-between"
              p="0"
              h="54px"
              bg="secondary-25"
              _hover={{}}
            >
              <Center
                w="54px"
                h="100%"
                borderRight="1px solid"
                borderColor="border-color"
              >
                <AccordionIcon />
              </Center>
              <Flex flex="1" alignItems="center">
                <Box px="12px">
                  <KText
                    size="md-medium"
                    fontWeight="550"
                    color="secondary-900"
                  >
                    Other scorecards
                  </KText>
                </Box>
                <Box
                  border="1px solid"
                  borderColor="border-color"
                  bg="base-white"
                  borderRadius="6px"
                  minWidth="24px"
                  minHeight="24px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p="0px 6px"
                >
                  <KText size="sm-medium">
                    {historyScorecards?.length || 0}
                  </KText>
                </Box>
              </Flex>
            </AccordionButton>
            <AccordionPanel p="0" overflow="hidden">
              {historySectionContent(historyScorecards, tableConfig)}
            </AccordionPanel>
          </AccordionItem>
        )}

      {historyScorecards?.length > 0 &&
        currentInterviewScorecards?.length === 0 && (
          <>{historySectionContent(historyScorecards, tableConfig)}</>
        )}
    </>
  );
};
